// External Libraries
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

// UI Components
import FormField from '../../components/ui/FormField';
import CheckboxField from '../../components/ui/CheckboxField';
import SelectField from '../../components/ui/SelectField';
import SubmitButton from '../../components/ui/SubmitButton';

// Redux Actions
import { getCurrenciesFunc } from '../../../store/custom-actions/currencies-actions/GetCurrenciesSlicer';
import { updateCurrencyFunc } from '../../../store/custom-actions/currencies-actions/UpdateCurrencySlicer';

function UpdateCurrencyForm({ show, onHide, currencyId, oldName, oldCode, oldSymbol, oldPosition, oldStatus, oldSortOrder }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  const { register, reset, formState: { errors }, handleSubmit, setValue } = useForm();
  const { loading: submitLoading } = useSelector(state => state.updateCurrencySlicer);

  // Populate form data when modal opens
  useEffect(() => {
    if (show && currencyId) {
      setValue("name", oldName || "");
      setValue("code", oldCode || "");
      setValue("symbol", oldSymbol || "");
      setValue("position", oldPosition || "after");
      setValue("is_active", oldStatus || false);
      setValue("sort_order", oldSortOrder || 0);
    }
  }, [show, currencyId, oldName, oldCode, oldSymbol, oldPosition, oldStatus, oldSortOrder, setValue]);

  const resetForm = () => {
    onHide();
    reset();
  };

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      is_active: data.is_active || false,
      sort_order: parseInt(data.sort_order) || 0,
    };
    
    const result = await dispatch(updateCurrencyFunc({ id: currencyId, data: payload }));
    
    if (updateCurrencyFunc.fulfilled.match(result)) {
      dispatch(getCurrenciesFunc());
      resetForm();
    }
  };

  const positionOptions = [
    { value: 'before', label: t('Before') },
    { value: 'after', label: t('After') },
  ];

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header>
          <Modal.Title>{t('Update Currency')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="submit-form-update"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row">
              <div className="col-md-6">
                <FormField
                  label={t("Name")}
                  name="name"
                  register={register}
                  required={true}
                  errors={errors}
                  placeholder={t("Enter currency name")}
                />
              </div>
              <div className="col-md-6">
                <FormField
                  label={t("Code")}
                  name="code"
                  register={register}
                  required={true}
                  errors={errors}
                  placeholder={t("Enter currency code (e.g., USD)")}
                  maxLength={3}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <FormField
                  label={t("Symbol")}
                  name="symbol"
                  register={register}
                  required={true}
                  errors={errors}
                  placeholder={t("Enter currency symbol")}
                />
              </div>
              <div className="col-md-6">
                <SelectField
                  label={t("Position")}
                  name="position"
                  register={register}
                  required={true}
                  errors={errors}
                  options={positionOptions}
                  placeholder={t("Select position")}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <FormField
                  label={t("Sort Order")}
                  name="sort_order"
                  type="number"
                  register={register}
                  errors={errors}
                  placeholder={t("Enter sort order")}
                  min="0"
                />
              </div>
              <div className="col-md-6 d-flex align-items-end">
                <CheckboxField
                  name="is_active"
                  label={t("Active")}
                  register={register}
                  errors={errors}
                />
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {t('Close')}
          </Button>
          <SubmitButton
            isLoading={submitLoading}
            form="submit-form-update"
          />
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default UpdateCurrencyForm;
